import { ChevronDown, ChevronUp, Grid3X3, PaintBucket, Pencil, RotateCcw, Ruler, Search } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as THREE from 'three';
import { API_URL } from "../../../config";

const WALL_TEXTURES = [
    { id: 'brick_wall_2_diff', name: 'Brick Wall 1' },
    { id: 'brick_wall_diff', name: 'Brick Wall 2' },
    { id: 'painted_brick_diff', name: 'Painted Brick' },
    { id: 'plastered_wall_2_diff', name: 'Plastered Wall 2' },
    { id: 'plastered_wall_diff', name: 'Plastered Wall' },
    { id: 'rounded_square_tiled_wall_diff', name: 'Square Tiled' },
    { id: 'stone_tile_wall_diff', name: 'Stone Tile' },
    { id: 'synthetic_wood_diff', name: 'Synthetic Wood' },
    { id: 'wood_plank_wall_diff', name: 'Wood Plank' },
];

const FLOOR_TEXTURES = [
    { id: 'grass_diff', name: 'Grass' },
    { id: 'asphalt_floor_diff', name: 'Asphalt' },
    { id: 'floor_tiles_diff', name: 'Floor Tiles' },
    { id: 'granite_tile_diff', name: 'Granite' },
    { id: 'marble_diff', name: 'Marble' },
    { id: 'plank_flooring_diff', name: 'Plank' },
    { id: 'rubber_tiles_diff', name: 'Rubber Tiles' },
    { id: 'square_floor_diff', name: 'Square Floor' },
    { id: 'tiled_floor_diffuse', name: 'Tiled Floor' },
    { id: 'wood_floor_diffuse', name: 'Wood Floor' },
];


interface Point {
    x: number;
    z: number;
}

interface Room {
    id: string;
    room_type: string;
    area: number;
    height: number;
    width: number;
    floor_polygon: Point[];
}

interface FloorPlanData {
    room_count: number;
    total_area: number;
    room_types: string[];
    rooms: Room[];
}

interface CustomizationPanelProps {
    onClose: () => void;
    scene?: THREE.Scene;
    renderer?: THREE.WebGLRenderer;
    floorPlanData?: FloorPlanData;
    camera?: THREE.PerspectiveCamera;
    onOverlaySettingsChange?: (settings: OverlaySettings) => void;
    onRoomMaterialChange?: (roomIds: string[], type: 'wall' | 'floor', material: { color?: string, texture?: string }) => void;
    onWallHeightChange?: (height: number) => void;
    onFloorPlanUpdate?: (updatedData: FloorPlanData) => void;
}

interface OverlaySettings {
    showOverlays: boolean;
    showTotalArea: boolean;
    showRoomCount: boolean;
    showRoomDetails: boolean;
    showRoomTypes: boolean;
    showDimensions: boolean;
    showPlotDimensions: boolean;
    showRoomAreas: boolean;
    showRoomLabels: boolean;
    overlayOpacity: number;
    overlaySize: 'small' | 'medium' | 'large';
}

// Collapsible Section Component
function CollapsibleSection({
    title,
    children,
    defaultExpanded = true,
    icon
}: {
    title: string,
    children: React.ReactNode,
    defaultExpanded?: boolean,
    icon?: React.ReactNode
}) {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    return (
        <div className="collapsible-section">
            <button
                className="collapsible-section__header"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="collapsible-section__title-group">
                    {icon && <span className="collapsible-section__icon">{icon}</span>}
                    <span className="collapsible-section__title">{title}</span>
                </div>
                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {isExpanded && (
                <div className="collapsible-section__content">
                    {children}
                </div>
            )}
        </div>
    );
}

export const CustomizationPanel: React.FC<CustomizationPanelProps> = ({
    onClose,
    scene,
    renderer,
    floorPlanData,
    camera,
    onOverlaySettingsChange,
    onRoomMaterialChange,
    onWallHeightChange,
    onFloorPlanUpdate
}) => {
    const [overlaySettings, setOverlaySettings] = useState<OverlaySettings>({
        showOverlays: false,
        showTotalArea: true,
        showRoomCount: true,
        showRoomDetails: false,
        showRoomTypes: true,
        showDimensions: true,
        showPlotDimensions: true,
        showRoomAreas: true,
        showRoomLabels: true,
        overlayOpacity: 0.9,
        overlaySize: 'medium'
    });

    const [selectedRooms, setSelectedRooms] = useState<Set<string>>(new Set());
    const [wallColor, setWallColor] = useState('#e1ded1');
    const [floorColor, setFloorColor] = useState('#f0f0f0');
    const [wallTexture, setWallTexture] = useState<string>('');
    const [floorTexture, setFloorTexture] = useState<string>('');
    const [wallTextureSearch, setWallTextureSearch] = useState('');
    const [floorTextureSearch, setFloorTextureSearch] = useState('');
    const [useWallTexture, setUseWallTexture] = useState(true);
    const [useFloorTexture, setUseFloorTexture] = useState(true);
    const [multiSelectMode, setMultiSelectMode] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [wallHeight, setWallHeight] = useState(40);
    const { projectId, planId } = useParams();

    const API_BASE_URL = API_URL;


    const panelRef = useRef<HTMLDivElement>(null);
    const wallHeightSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
// Load customization data from floorPlanData on mount
useEffect(() => {
    if (floorPlanData && (floorPlanData as any).customization) {
        const customization = (floorPlanData as any).customization;
        
        // Load wall height
        if (customization.wallHeight !== undefined) {
            setWallHeight(customization.wallHeight);
        }
        
        // Load wall customization - check if any room has texture or color
        if (customization.wall && Object.keys(customization.wall).length > 0) {
            const firstRoomWithWall = Object.values(customization.wall)[0] as any;
            if (firstRoomWithWall?.color) {
                setWallColor(firstRoomWithWall.color);
                setUseWallTexture(false);
            }
            if (firstRoomWithWall?.texture) {
                setWallTexture(firstRoomWithWall.texture);
                setUseWallTexture(true);
            }
        }
        
        // Load floor customization - check if any room has texture or color
        if (customization.floor && Object.keys(customization.floor).length > 0) {
            const firstRoomWithFloor = Object.values(customization.floor)[0] as any;
            if (firstRoomWithFloor?.color) {
                setFloorColor(firstRoomWithFloor.color);
                setUseFloorTexture(false);
            }
            if (firstRoomWithFloor?.texture) {
                setFloorTexture(firstRoomWithFloor.texture);
                setUseFloorTexture(true);
            }
        }
    }
}, [floorPlanData]);

    const blockAllEvents = (e: React.MouseEvent | React.WheelEvent) => {
        e.stopPropagation();
    };

    useEffect(() => {
        const panel = panelRef.current;
        if (!panel) return;

        const handleWheel = (e: WheelEvent) => {
            // Only stop propagation to prevent 3D scene interaction
            // Don't prevent default to allow panel scrolling
            e.stopPropagation();
        };

        panel.addEventListener('wheel', handleWheel, { passive: true });
        return () => panel.removeEventListener('wheel', handleWheel);
    }, []);

    // Cleanup wall height timer on unmount
    useEffect(() => {
        return () => {
            if (wallHeightSaveTimerRef.current) {
                clearTimeout(wallHeightSaveTimerRef.current);
            }
        };
    }, []);

    // Calculate room statistics
    const roomStats = floorPlanData ? {
        totalArea: floorPlanData.total_area,
        roomCount: floorPlanData.room_count,
        roomTypes: floorPlanData.room_types,
        rooms: floorPlanData.rooms.filter(room => room.room_type !== 'Wall' && room.room_type !== 'Boundary'),
        largestRoom: floorPlanData.rooms.filter(room => room.room_type !== 'Wall' && room.room_type !== 'Boundary').reduce((largest, room) =>
            room.area > largest.area ? room : largest,
            { area: 0, room_type: '', width: 0, height: 0 }
        ),
        smallestRoom: floorPlanData.rooms.filter(room => room.room_type !== 'Wall' && room.room_type !== 'Boundary').reduce((smallest, room) =>
            room.area < smallest.area ? room : smallest,
            { area: Infinity, room_type: '', width: 0, height: 0 }
        )
    } : null;

    const updateOverlaySetting = <K extends keyof OverlaySettings>(
        key: K,
        value: OverlaySettings[K]
    ) => {
        const newSettings = { ...overlaySettings, [key]: value };
        setOverlaySettings(newSettings);
        onOverlaySettingsChange?.(newSettings);
    };

    // Calculate plot dimensions
    const plotDimensions = floorPlanData && floorPlanData.rooms && floorPlanData.rooms.length > 0 ? (() => {
        let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;

        floorPlanData.rooms.forEach(room => {
            if (room.floor_polygon && room.floor_polygon.length > 0) {
                room.floor_polygon.forEach(point => {
                    minX = Math.min(minX, point.x);
                    maxX = Math.max(maxX, point.x);
                    minZ = Math.min(minZ, point.z);
                    maxZ = Math.max(maxZ, point.z);
                });
            }
        });

        // Check if we found valid dimensions
        if (minX === Infinity || maxX === -Infinity || minZ === Infinity || maxZ === -Infinity) {
            return null;
        }

        const width = maxX - minX;
        const length = maxZ - minZ;

        // Add padding (10% on each side)
        const padding = Math.max(width, length) * 0.1;

        return {
            minX: minX - padding,
            maxX: maxX + padding,
            minZ: minZ - padding,
            maxZ: maxZ + padding,
            width: width + (padding * 2),
            length: length + (padding * 2),
            area: width * length
        };
    })() : null;

    const handleRoomClick = (roomId: string, event: React.MouseEvent) => {
        const newSelected = new Set(selectedRooms);

        // Support both keyboard modifiers (desktop) and multi-select mode (mobile)
        if (event.ctrlKey || event.metaKey || multiSelectMode) {
            if (newSelected.has(roomId)) {
                newSelected.delete(roomId);
            } else {
                newSelected.add(roomId);
            }
        } else {
            newSelected.clear();
            newSelected.add(roomId);
        }

        setSelectedRooms(newSelected);
    };

    const handleSelectAll = () => {
        const allRoomIds = floorPlanData?.rooms
            .filter(room => room.room_type !== 'Wall')
            .map(room => room.id) || [];
        setSelectedRooms(new Set(allRoomIds));
    };

    const handleDeselectAll = () => {
        setSelectedRooms(new Set());
    };

    const handleWallHeightChange = async (newHeight: number) => {
        setWallHeight(newHeight);
        
        // Immediately update 3D scene
        if (onWallHeightChange) {
            onWallHeightChange(newHeight);
        }
        
        // Clear existing timer
        if (wallHeightSaveTimerRef.current) {
            clearTimeout(wallHeightSaveTimerRef.current);
        }
        
        // Set new timer to save after 200ms of no changes
        wallHeightSaveTimerRef.current = setTimeout(async () => {
            await saveWallHeightToBackend(newHeight);
        }, 200);
    };

    const saveWallHeightToBackend = async (newHeight: number) => {
        if (!projectId || !planId || !floorPlanData) return;

        try {
            setIsSaving(true);
            const token = localStorage.getItem('access_token');
            if (!token) {
                console.error('No access token found');
                return;
            }

            const currentCustomization = (floorPlanData as any)?.customization || { wall: {}, floor: {} };
            const updatedCustomization = { 
                ...currentCustomization, 
                wallHeight: newHeight 
            };

            const updatedFloorPlanData = {
                ...floorPlanData,
                customization: updatedCustomization
            } as FloorPlanData;

            // Update localStorage
            const cachedProjects = localStorage.getItem("userProjects");
            if (cachedProjects) {
                const projects = JSON.parse(cachedProjects);
                const updatedProjects = projects.map((project: any) =>
                    project.id.toString() === projectId
                        ? {
                            ...project,
                            plans: project.plans.map((plan: any) =>
                                plan.id.toString() === planId
                                    ? { ...plan, coordinates: updatedFloorPlanData }
                                    : plan
                            )
                        }
                        : project
                );
                localStorage.setItem("userProjects", JSON.stringify(updatedProjects));
            }

            const cachedCurrentPlan = localStorage.getItem("currentPlan");
            if (cachedCurrentPlan) {
                const currentPlans = JSON.parse(cachedCurrentPlan);
                const planKey = `${projectId}/${planId}`;
                if (currentPlans[planKey]) {
                    currentPlans[planKey].coordinates = updatedFloorPlanData;
                    localStorage.setItem("currentPlan", JSON.stringify(currentPlans));
                }
            }

            const coordinatesPayload = {
                coordinates: updatedFloorPlanData
            };

            const response = await fetch(
                `${API_BASE_URL}/projects/${projectId}/plans/${planId}/`,
                {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(coordinatesPayload)
                }
            );

            if (response.ok) {
                const updatedPlan = await response.json();
                if (onFloorPlanUpdate) {
                    onFloorPlanUpdate(updatedFloorPlanData);
                }
            }
        } catch (error) {
            console.error('Error saving wall height:', error);
        } finally {
            setIsSaving(false);
        }
    };

    // API call to save customization to backend
    const saveCustomizationToBackend = async (customizationData: {
        wallColor?: string;
        wallTexture?: string;
        floorColor?: string;
        floorTexture?: string;
        roomIds?: string[];
        type: 'wall' | 'floor';
    }) => {
        if (!projectId || !planId) {
            console.error('Missing projectId or planId');
            return { status: false, data: null };
        }

        try {
            setIsSaving(true);
            const token = localStorage.getItem('access_token');
            if (!token) {
                console.error('No access token found');
                return { status: false, data: null };
            }

            // Get current customization or create new one
            const currentCustomization = (floorPlanData as any)?.customization || { wall: {}, floor: {} };
            
            // Update customization per room
            const updatedCustomization = { ...currentCustomization };
            
            if (customizationData.type === 'wall') {
                if (!updatedCustomization.wall) {
                    updatedCustomization.wall = {};
                }
                // Apply the same customization to all selected rooms
                customizationData.roomIds?.forEach(roomId => {
                    updatedCustomization.wall[roomId] = {
                        ...(customizationData.wallTexture ? { texture: customizationData.wallTexture } : {}),
                        ...(customizationData.wallColor ? { color: customizationData.wallColor } : {})
                    };
                });
            } else if (customizationData.type === 'floor') {
                if (!updatedCustomization.floor) {
                    updatedCustomization.floor = {};
                }
                // Apply the same customization to all selected rooms
                customizationData.roomIds?.forEach(roomId => {
                    updatedCustomization.floor[roomId] = {
                        ...(customizationData.floorTexture ? { texture: customizationData.floorTexture } : {}),
                        ...(customizationData.floorColor ? { color: customizationData.floorColor } : {})
                    };
                });
            }

            // Merge with floor plan data
            const updatedFloorPlanData = {
                ...floorPlanData,
                customization: updatedCustomization
            } as FloorPlanData;

            // ===== UPDATE LOCAL STORAGE IMMEDIATELY =====
            // Update userProjects in localStorage
            const cachedProjects = localStorage.getItem("userProjects");
            if (cachedProjects) {
                const projects = JSON.parse(cachedProjects);
                const updatedProjects = projects.map((project: any) =>
                    project.id.toString() === projectId
                        ? {
                            ...project,
                            plans: project.plans.map((plan: any) =>
                                plan.id.toString() === planId
                                    ? { ...plan, coordinates: updatedFloorPlanData }
                                    : plan
                            )
                        }
                        : project
                );
                localStorage.setItem("userProjects", JSON.stringify(updatedProjects));
                // console.log('Updated userProjects in localStorage');
            }

            // Update currentPlan in localStorage
            const cachedCurrentPlan = localStorage.getItem("currentPlan");
            if (cachedCurrentPlan) {
                const currentPlans = JSON.parse(cachedCurrentPlan);
                const planKey = `${projectId}/${planId}`;
                if (currentPlans[planKey]) {
                    currentPlans[planKey].coordinates = updatedFloorPlanData;
                    localStorage.setItem("currentPlan", JSON.stringify(currentPlans));
                    // console.log('Updated currentPlan in localStorage');
                }
            }

            const coordinatesPayload = {
                coordinates: updatedFloorPlanData
            };

            const response = await fetch(
                `${API_BASE_URL}/projects/${projectId}/plans/${planId}/`,
                {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(coordinatesPayload)
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                console.error('Failed to update plan:', errorData);
                return { status: false, data: null };
            }

            const updatedPlan = await response.json();

            // Call the callback to update parent component
            if (onFloorPlanUpdate) {
                onFloorPlanUpdate(updatedFloorPlanData);
            }

            return { status: true, data: updatedPlan };

        } catch (error) {
            console.error('Error saving customization:', error);
            return { status: false, data: null };
        } finally {
            setIsSaving(false);
        }
    };
    const applyWallMaterial = async () => {
        if (selectedRooms.size === 0 || !onRoomMaterialChange) return;

        const material: any = {};
        if (useWallTexture && wallTexture) {
            material.texture = wallTexture;
        } else {
            material.color = wallColor;
        }

        // Apply to 3D scene
        onRoomMaterialChange(Array.from(selectedRooms), 'wall', material);

        // Save to backend
        const result = await saveCustomizationToBackend({
            wallColor: !useWallTexture ? wallColor : undefined,
            wallTexture: useWallTexture ? wallTexture : undefined,
            roomIds: Array.from(selectedRooms),
            type: 'wall'
        });

        if (result.status) {
            // console.log('Wall customization saved successfully');
        } else {
            alert('Failed to save wall customization to server');
        }
    };

    const applyFloorMaterial = async () => {
        if (selectedRooms.size === 0 || !onRoomMaterialChange) return;

        const material: any = {};
        if (useFloorTexture && floorTexture) {
            material.texture = floorTexture;
        } else {
            material.color = floorColor;
        }

        // Apply to 3D scene
        onRoomMaterialChange(Array.from(selectedRooms), 'floor', material);

        // Save to backend
        const result = await saveCustomizationToBackend({
            floorColor: !useFloorTexture ? floorColor : undefined,
            floorTexture: useFloorTexture ? floorTexture : undefined,
            roomIds: Array.from(selectedRooms),
            type: 'floor'
        });

        if (result.status) {
            // console.log('Floor customization saved successfully');
        } else {
            alert('Failed to save floor customization to server');
        }
    };

    const filteredWallTextures = WALL_TEXTURES.filter(tex =>
        tex.name.toLowerCase().includes(wallTextureSearch.toLowerCase())
    );

    const filteredFloorTextures = FLOOR_TEXTURES.filter(tex =>
        tex.name.toLowerCase().includes(floorTextureSearch.toLowerCase())
    );

    return (
        <div
            ref={panelRef}
            className="modern-panel"
            onMouseDown={blockAllEvents}
            onMouseMove={blockAllEvents}
            onMouseUp={blockAllEvents}
            onClick={blockAllEvents}
            onDoubleClick={blockAllEvents}
            onWheel={blockAllEvents}
        >
            <div className="modern-panel__header">
                <div className="modern-panel__header-content">
                    <Pencil size={20} className="modern-panel__header-icon" />
                    <h2 className="modern-panel__title">Customization</h2>
                </div>
                <button onClick={onClose} className="modern-panel__close">
                    x
                </button>
            </div>

            <div className="modern-panel__content">
                {floorPlanData && (
                    <>
                        {/* Wall Material - applies to all walls */}
                        <CollapsibleSection
                            title="Wall Customization"
                            defaultExpanded={false}
                            icon={<PaintBucket size={16} />}
                        >
                            <div className="section-group">
                                <p className="section-description">Adjust color or texture for all walls</p>

                                <div className="material-type-toggle">
                                    <button
                                        className={`toggle-btn ${useWallTexture ? 'active' : ''}`}
                                        onClick={() => setUseWallTexture(true)}
                                    >
                                        Texture
                                    </button>
                                    <button
                                        className={`toggle-btn ${!useWallTexture ? 'active' : ''}`}
                                        onClick={() => setUseWallTexture(false)}
                                    >
                                        Color
                                    </button>
                                </div>

                                {!useWallTexture ? (
                                    <div className="color-picker">
                                        <label className="color-picker__label">Wall Color</label>
                                        <input
                                            type="color"
                                            value={wallColor}
                                            onChange={(e) => setWallColor(e.target.value)}
                                            className="color-picker__input"
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <div className="texture-search">
                                            <Search size={16} />
                                            <input
                                                type="text"
                                                placeholder="Search wall textures..."
                                                value={wallTextureSearch}
                                                onChange={(e) => setWallTextureSearch(e.target.value)}
                                                className="search-input"
                                            />
                                        </div>
                                        <div className="texture-grid">
                                            {filteredWallTextures.map(tex => (
                                                <div
                                                    key={tex.id}
                                                    className={`texture-item ${wallTexture === tex.id ? 'selected' : ''}`}
                                                    onClick={() => setWallTexture(tex.id)}
                                                >
                                                    <div
                                                        className="texture-preview"
                                                        style={{
                                                            backgroundImage: `url(/textures/${tex.id}.jpg)`,
                                                            backgroundSize: 'cover'
                                                        }}
                                                    />
                                                    <span className="texture-name">{tex.name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}

                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button 
                                        className="apply-btn" 
                                        style={{ flex: 1 }}
                                        onClick={async () => {
                                            // Apply to all rooms including individual walls
                                            const allRoomIds = floorPlanData?.rooms.map(room => room.id) || [];
                                            if (allRoomIds.length > 0 && onRoomMaterialChange) {
                                                const material: any = {};
                                                if (useWallTexture && wallTexture) {
                                                    material.texture = wallTexture;
                                                } else {
                                                    material.color = wallColor;
                                                }
                                                
                                                // Apply to 3D scene
                                                onRoomMaterialChange(allRoomIds, 'wall', material);
                                                
                                                // Save to backend
                                                const result = await saveCustomizationToBackend({
                                                    wallColor: !useWallTexture ? wallColor : undefined,
                                                    wallTexture: useWallTexture ? wallTexture : undefined,
                                                    roomIds: allRoomIds,
                                                    type: 'wall'
                                                });

                                                if (result.status) {
                                                    // console.log('Wall customization saved successfully');
                                                } else {
                                                    alert('Failed to save wall customization to server');
                                                }
                                            }
                                        }}
                                        disabled={isSaving}
                                    >
                                        {isSaving ? 'Saving...' : 'Apply'}
                                    </button>
                                    <button 
                                        className="reset-btn"
                                        onClick={async () => {
                                            const resetColor = '#dacba5';
                                            setWallColor(resetColor);
                                            setUseWallTexture(false);
                                            setWallTexture('');
                                            
                                            // Apply reset to all rooms including individual walls
                                            const allRoomIds = floorPlanData?.rooms.map(room => room.id) || [];
                                            if (allRoomIds.length > 0 && onRoomMaterialChange) {
                                                onRoomMaterialChange(allRoomIds, 'wall', { color: resetColor });
                                                
                                                // Save to backend
                                                await saveCustomizationToBackend({
                                                    wallColor: resetColor,
                                                    roomIds: allRoomIds,
                                                    type: 'wall'
                                                });
                                            }
                                        }}
                                        disabled={isSaving}
                                        title="Reset to default"
                                    >
                                        <RotateCcw size={16} />
                                    </button>
                                </div>
                            </div>
                        </CollapsibleSection>

                        {/* Wall Height */}
                        <CollapsibleSection
                            title="Wall Height"
                            defaultExpanded={false}
                            icon={<Ruler size={16} />}
                        >
                            <div className="section-group">
                                <p className="section-description">Adjust the height of all walls</p>
                                
                                <div className="height-control">
                                    <label className="height-control__label">
                                        Height: {wallHeight} units
                                    </label>
                                    <input
                                        type="range"
                                        min="10"
                                        max="60"
                                        step="1"
                                        value={wallHeight}
                                        onChange={(e) => handleWallHeightChange(Number(e.target.value))}
                                        className="height-control__slider"
                                    />
                                    <div className="height-control__markers">
                                        <span>10</span>
                                        <span>35</span>
                                        <span>60</span>
                                    </div>
                                </div>
                                
                                <button 
                                    className="reset-btn"
                                    style={{ width: '100%', marginTop: '12px' }}
                                    onClick={() => handleWallHeightChange(40)}
                                    disabled={isSaving}
                                    title="Reset to default (40 units)"
                                >
                                    <RotateCcw size={16} />
                                    <span style={{ marginLeft: '8px' }}>Reset Height</span>
                                </button>
                            </div>
                        </CollapsibleSection>

                        {/* Floor Material - with room selector */}
                        <CollapsibleSection
                            title="Floor Customization"
                            defaultExpanded={false}
                            icon={<Grid3X3 size={16} />}
                        >
                            <div className="section-group">
                                <p className="section-description">Select rooms on the map to customize floors</p>

                                {/* Room Selector Map */}
                                <div className="room-selector-map">
                                    {plotDimensions && (
                                        <svg
                                            viewBox={`${plotDimensions.minX} ${plotDimensions.minZ} ${plotDimensions.width} ${plotDimensions.length}`}
                                            className="room-map-svg"
                                        >
                                            {floorPlanData.rooms
                                                .filter(room => room.room_type !== 'Wall' && room.room_type !== 'Boundary')
                                                .map(room => {
                                                    const isSelected = selectedRooms.has(room.id);
                                                    const points = room.floor_polygon.map(p => `${p.x},${p.z}`).join(' ');

                                                    return (
                                                        <polygon
                                                            key={room.id}
                                                            points={points}
                                                            fill={isSelected ? '#3b82f6' : '#ffffff'}
                                                            stroke="#475569"
                                                            strokeWidth="2"
                                                            style={{ cursor: 'pointer', transition: 'fill 0.2s' }}
                                                            onClick={(e) => handleRoomClick(room.id, e as any)}
                                                            onMouseEnter={(e) => {
                                                                if (!isSelected) {
                                                                    e.currentTarget.setAttribute('fill', '#e0f2fe');
                                                                }
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                if (!isSelected) {
                                                                    e.currentTarget.setAttribute('fill', '#ffffff');
                                                                }
                                                            }}
                                                        />
                                                    );
                                                })}
                                        </svg>
                                    )}
                                    {!plotDimensions && (
                                        <div className="room-map-placeholder">
                                            Loading floor plan...
                                        </div>
                                    )}
                                    <div className="room-selector-controls">
                                        <button className="selector-btn" onClick={handleSelectAll}>Select All</button>
                                        <button className="selector-btn" onClick={handleDeselectAll}>Deselect All</button>
                                        <button
                                            className={`selector-btn multi-select-toggle ${multiSelectMode ? 'active' : ''}`}
                                            onClick={() => setMultiSelectMode(!multiSelectMode)}
                                        >
                                            Multi-Select
                                        </button>
                                        <span className="selector-hint">Hold Ctrl/Cmd or use Multi-Select</span>
                                    </div>
                                </div>

                                {selectedRooms.size > 0 && (
                                    <div className="material-section">
                                        <div className="material-type-toggle">
                                            <button
                                                className={`toggle-btn ${useFloorTexture ? 'active' : ''}`}
                                                onClick={() => setUseFloorTexture(true)}
                                            >
                                                Texture
                                            </button>
                                            <button
                                                className={`toggle-btn ${!useFloorTexture ? 'active' : ''}`}
                                                onClick={() => setUseFloorTexture(false)}
                                            >
                                                Color
                                            </button>
                                        </div>

                                        {!useFloorTexture ? (
                                            <div className="color-picker">
                                                <label className="color-picker__label">Floor Color</label>
                                                <input
                                                    type="color"
                                                    value={floorColor}
                                                    onChange={(e) => setFloorColor(e.target.value)}
                                                    className="color-picker__input"
                                                />
                                            </div>
                                        ) : (
                                            <>
                                                <div className="texture-search">
                                                    <Search size={16} />
                                                    <input
                                                        type="text"
                                                        placeholder="Search floor textures..."
                                                        value={floorTextureSearch}
                                                        onChange={(e) => setFloorTextureSearch(e.target.value)}
                                                        className="search-input"
                                                    />
                                                </div>
                                                <div className="texture-grid">
                                                    {filteredFloorTextures.map(tex => (
                                                        <div
                                                            key={tex.id}
                                                            className={`texture-item ${floorTexture === tex.id ? 'selected' : ''}`}
                                                            onClick={() => setFloorTexture(tex.id)}
                                                        >
                                                            <div
                                                                className="texture-preview"
                                                                style={{
                                                                    backgroundImage: `url(/textures/${tex.id}.jpg)`,
                                                                    backgroundSize: 'cover'
                                                                }}
                                                            />
                                                            <span className="texture-name">{tex.name}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        )}

                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button 
                                                className="apply-btn" 
                                                style={{ flex: 1 }}
                                                onClick={applyFloorMaterial}
                                                disabled={isSaving}
                                            >
                                                {isSaving ? 'Saving...' : `Apply to ${selectedRooms.size} Room${selectedRooms.size > 1 ? 's' : ''}`}
                                            </button>
                                            <button 
                                                className="reset-btn"
                                                onClick={async () => {
                                                    const resetColor = '#d8d5d5';
                                                    setFloorColor(resetColor);
                                                    setUseFloorTexture(false);
                                                    setFloorTexture('');
                                                    
                                                    // Apply reset to selected rooms
                                                    const roomIds = Array.from(selectedRooms);
                                                    if (roomIds.length > 0 && onRoomMaterialChange) {
                                                        onRoomMaterialChange(roomIds, 'floor', { color: resetColor });
                                                        
                                                        // Save to backend
                                                        await saveCustomizationToBackend({
                                                            floorColor: resetColor,
                                                            roomIds: roomIds,
                                                            type: 'floor'
                                                        });
                                                    }
                                                }}
                                                disabled={isSaving}
                                                title="Reset to default"
                                            >
                                                <RotateCcw size={16} />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </CollapsibleSection>

                        {/* <CollapsibleSection
                            title="3D Information Overlays"
                            defaultExpanded={false}
                            icon={<Grid3X3 size={16} />}
                        >
                            <div className="section-group">
                                <div className="master-toggle">
                                    <label className="modern-checkbox large">
                                        <input
                                            type="checkbox"
                                            checked={overlaySettings.showOverlays}
                                            onChange={(e) => updateOverlaySetting('showOverlays', e.target.checked)}
                                        />
                                        <span className="modern-checkbox__checkmark"></span>
                                        <span className="modern-checkbox__label">Enable 3D Information Overlays</span>
                                    </label>
                                </div>

                                {overlaySettings.showOverlays && (
                                    <>
                                        <h4 className="section-subtitle">What to Display</h4>
                                        <div className="overlay-toggles">
                                            <label className="modern-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={overlaySettings.showRoomLabels}
                                                    onChange={(e) => updateOverlaySetting('showRoomLabels', e.target.checked)}
                                                />
                                                <span className="modern-checkbox__checkmark"></span>
                                                <span className="modern-checkbox__label">Room Names</span>
                                            </label>

                                            <label className="modern-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={overlaySettings.showRoomAreas}
                                                    onChange={(e) => updateOverlaySetting('showRoomAreas', e.target.checked)}
                                                />
                                                <span className="modern-checkbox__checkmark"></span>
                                                <span className="modern-checkbox__label">Room Areas</span>
                                            </label>

                                            <label className="modern-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={overlaySettings.showDimensions}
                                                    onChange={(e) => updateOverlaySetting('showDimensions', e.target.checked)}
                                                />
                                                <span className="modern-checkbox__checkmark"></span>
                                                <span className="modern-checkbox__label">Room Dimensions</span>
                                            </label>

                                            <label className="modern-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={overlaySettings.showPlotDimensions}
                                                    onChange={(e) => updateOverlaySetting('showPlotDimensions', e.target.checked)}
                                                />
                                                <span className="modern-checkbox__checkmark"></span>
                                                <span className="modern-checkbox__label">Plot Dimensions</span>
                                            </label>

                                            <label className="modern-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={overlaySettings.showTotalArea}
                                                    onChange={(e) => updateOverlaySetting('showTotalArea', e.target.checked)}
                                                />
                                                <span className="modern-checkbox__checkmark"></span>
                                                <span className="modern-checkbox__label">Total Plot Area</span>
                                            </label>

                                            <label className="modern-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={overlaySettings.showRoomCount}
                                                    onChange={(e) => updateOverlaySetting('showRoomCount', e.target.checked)}
                                                />
                                                <span className="modern-checkbox__checkmark"></span>
                                                <span className="modern-checkbox__label">Room Count</span>
                                            </label>
                                        </div>

                                        <h4 className="section-subtitle">Overlay Appearance</h4>

                                        <div className="control-group">
                                            <label className="control-label">Overlay Size</label>
                                            <div className="size-buttons">
                                                {(['small', 'medium', 'large'] as const).map(size => (
                                                    <button
                                                        key={size}
                                                        className={`size-btn ${overlaySettings.overlaySize === size ? 'active' : ''}`}
                                                        onClick={() => updateOverlaySetting('overlaySize', size)}
                                                    >
                                                        {size.charAt(0).toUpperCase() + size.slice(1)}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="control-group">
                                            <label className="control-label">Overlay Opacity</label>
                                            <div className="slider-container">
                                                <input
                                                    type="range"
                                                    min="0.3"
                                                    max="1"
                                                    step="0.1"
                                                    value={overlaySettings.overlayOpacity}
                                                    onChange={(e) => updateOverlaySetting('overlayOpacity', parseFloat(e.target.value))}
                                                    className="modern-slider"
                                                />
                                                <span className="slider-value">
                                                    {Math.round(overlaySettings.overlayOpacity * 100)}%
                                                </span>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </CollapsibleSection>

                        <CollapsibleSection
                            title="Statistics"
                            defaultExpanded={false}
                            icon={<BarChart3 size={16} />}
                        >
                            <div className="section-group">
                                {roomStats && (
                                    <div className="statistics-grid">
                                        {roomStats.totalArea != null && (
                                            <div className="stat-card">
                                                <div className="stat-number">{roomStats.totalArea.toFixed(1)}</div>
                                                <div className="stat-label">Total Area (mÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â²)</div>
                                            </div>
                                        )}

                                        {roomStats.roomCount != null && (
                                            <div className="stat-card">
                                                <div className="stat-number">{roomStats.roomCount}</div>
                                                <div className="stat-label">Total Rooms</div>
                                            </div>
                                        )}

                                        {roomStats.roomTypes && roomStats.roomTypes.length > 0 && (
                                            <div className="stat-card">
                                                <div className="stat-number">{roomStats.roomTypes.length}</div>
                                                <div className="stat-label">Room Types</div>
                                            </div>
                                        )}

                                        {roomStats.largestRoom && roomStats.largestRoom.area > 0 && (
                                            <div className="stat-card">
                                                <div className="stat-number">{roomStats.largestRoom.area.toFixed(1)}</div>
                                                <div className="stat-label">Largest Room (mÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â²)</div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </CollapsibleSection> */}
                    </>
                )}
            </div>

            {/* Styles */}
            <style>{`
                /* Custom Scrollbar Styles */
                .modern-panel {
                    scrollbar-width: thin;
                    scrollbar-color: #475569 #e2e8f0;
                }

                .modern-panel::-webkit-scrollbar {
                    width: 12px;
                }

                .modern-panel::-webkit-scrollbar-track {
                    background: #e2e8f0;
                    border-radius: 6px;
                }

                .modern-panel::-webkit-scrollbar-thumb {
                    background: #475569;
                    border-radius: 6px;
                    border: 2px solid #e2e8f0;
                }

                .modern-panel::-webkit-scrollbar-thumb:hover {
                    background: #334155;
                }

                .modern-panel::-webkit-scrollbar-thumb:active {
                    background: #1e293b;
                }

                .modern-panel__content {
                    scrollbar-width: thin;
                    scrollbar-color: #475569 #e2e8f0;
                }

                .modern-panel__content::-webkit-scrollbar {
                    width: 12px;
                }

                .modern-panel__content::-webkit-scrollbar-track {
                    background: #e2e8f0;
                    border-radius: 6px;
                }

                .modern-panel__content::-webkit-scrollbar-thumb {
                    background: #475569;
                    border-radius: 6px;
                    border: 2px solid #e2e8f0;
                }

                .modern-panel__content::-webkit-scrollbar-thumb:hover {
                    background: #334155;
                }

                .modern-panel__content::-webkit-scrollbar-thumb:active {
                    background: #1e293b;
                }

                .collapsible-section {
                    border-bottom: 1px solid rgba(224, 231, 255, 0.3);
                }

                .collapsible-section__header {
                    width: 100%;
                    background: none;
                    border: none;
                    padding: 20px 24px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    text-align: left;
                }

                .collapsible-section__header:hover {
                    background: rgba(59, 130, 246, 0.05);
                }

                .collapsible-section__title-group {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .collapsible-section__icon {
                    color: #3b82f6;
                }

                .collapsible-section__title {
                    font-size: 16px;
                    font-weight: 600;
                    color: #1e293b;
                    letter-spacing: -0.025em;
                }

                .collapsible-section__content {
                    padding: 0 24px 20px;
                    animation: slideDown 0.3s ease;
                }

                .section-group {
                    margin-bottom: 0;
                }

                .section-subtitle {
                    font-size: 14px;
                    font-weight: 600;
                    color: #475569;
                    margin: 0 0 16px 0;
                    letter-spacing: -0.025em;
                }

                .info-subtitle {
                    font-size: 13px;
                    font-weight: 600;
                    color: #475569;
                    margin: 16px 0 12px 0;
                    letter-spacing: -0.025em;
                }

                /* Info toggles */
                .info-toggles {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                    margin-bottom: 20px;
                }

                /* Statistics grid */
                .statistics-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px;
                }

                .stat-card {
                    background: #f8fafc;
                    border-radius: 12px;
                    padding: 16px;
                    text-align: center;
                    border: 1px solid #e2e8f0;
                    transition: all 0.2s ease;
                }

                .stat-card:hover {
                    background: #eff6ff;
                    border-color: #3b82f6;
                    transform: translateY(-2px);
                }

                .stat-number {
                    font-size: 24px;
                    font-weight: 700;
                    color: #1e293b;
                    margin-bottom: 4px;
                }

                .stat-label {
                    font-size: 11px;
                    color: #64748b;
                    font-weight: 500;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }
                
                .selected-indicator {
                    position: absolute;
                    top: -4px;
                    right: -4px;
                    width: 20px;
                    height: 20px;
                    background: #10b981;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 12px;
                    font-weight: bold;
                }

                .loading-indicator {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 16px;
                    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
                    border-radius: 10px;
                    margin-bottom: 16px;
                }

                .spinner {
                    width: 20px;
                    height: 20px;
                    border: 2px solid #e2e8f0;
                    border-top: 2px solid #3b82f6;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .intensity-control, .background-mode-control {
                    margin-top: 20px;
                }

                .control-label {
                    display: block;
                    font-size: 12px;
                    color: #64748b;
                    margin-bottom: 8px;
                    font-weight: 500;
                }

                .slider-container {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .modern-slider {
                    flex: 1;
                    height: 6px;
                    border-radius: 3px;
                    background: #e2e8f0;
                    outline: none;
                    transition: background 0.2s ease;
                    -webkit-appearance: none;
                    appearance: none;
                }

                .modern-slider::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    width: 20px;
                    height: 20px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
                    cursor: pointer;
                    transition: all 0.2s ease;
                    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
                }

                .modern-slider::-webkit-slider-thumb:hover {
                    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
                    transform: scale(1.1);
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
                }

                .modern-slider::-moz-range-thumb {
                    width: 20px;
                    height: 20px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
                    cursor: pointer;
                    border: none;
                    transition: all 0.2s ease;
                    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
                }

                .modern-slider::-moz-range-thumb:hover {
                    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
                    transform: scale(1.1);
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
                }

                .slider-value {
                    min-width: 50px;
                    text-align: center;
                    font-size: 12px;
                    padding: 6px 10px;
                    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
                    border-radius: 6px;
                    color: #1e293b;
                    font-weight: 500;
                    border: 1px solid #e2e8f0;
                }

                .toggle-buttons {
                    display: flex;
                    gap: 8px;
                    margin-top: 8px;
                }

                .toggle-btn {
                    flex: 1;
                    padding: 10px 16px;
                    border: 2px solid #e2e8f0;
                    border-radius: 8px;
                    background: #ffffff;
                    cursor: pointer;
                    font-size: 12px;
                    font-weight: 500;
                    color: #374151;
                    transition: all 0.2s ease;
                    text-align: center;
                }

                .toggle-btn:hover {
                    border-color: #3b82f6;
                    background: #eff6ff;
                    color: #1e40af;
                }

                .toggle-btn.active {
                    border-color: #3b82f6;
                    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
                    color: white;
                    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
                }

                .modern-checkbox {
                    display: flex;
                    align-items: center;
                    cursor: pointer;
                    gap: 12px;
                }

                .modern-checkbox input {
                    position: absolute;
                    opacity: 0;
                }

                .modern-checkbox__checkmark {
                    width: 20px;
                    height: 20px;
                    border: 2px solid #d1d5db;
                    border-radius: 6px;
                    background: #ffffff;
                    position: relative;
                    transition: all 0.2s ease;
                    flex-shrink: 0;
                }

                .modern-checkbox input:checked + .modern-checkbox__checkmark {
                    background: #3b82f6;
                    border-color: #3b82f6;
                }

                .modern-checkbox input:checked + .modern-checkbox__checkmark::after {
                    content: '';
                    position: absolute;
                    left: 6px;
                    top: 2px;
                    width: 6px;
                    height: 10px;
                    border: solid white;
                    border-width: 0 2px 2px 0;
                    transform: rotate(45deg);
                }

                .modern-checkbox__label {
                    font-size: 14px;
                    color: #374151;
                    font-weight: 500;
                }

                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                /* Responsive Design */
                @media (max-width: 768px) {
                    .statistics-grid {
                        grid-template-columns: 1fr;
                    }
                }

                /* 3D Overlay Styles */
                .master-toggle {
                    margin-bottom: 20px;
                    padding: 16px;
                    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
                    border-radius: 12px;
                    border: 1px solid #3b82f6;
                }

                .modern-checkbox.large .modern-checkbox__label {
                    font-size: 16px;
                    font-weight: 600;
                    color: #1e40af;
                }

                .overlay-toggles {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 12px;
                    margin-bottom: 20px;
                }

                .control-group {
                    margin-bottom: 20px;
                }

                .size-buttons {
                    display: flex;
                    gap: 8px;
                    margin-top: 8px;
                }

                .size-btn {
                    flex: 1;
                    padding: 8px 12px;
                    border: 2px solid #e2e8f0;
                    border-radius: 8px;
                    background: #ffffff;
                    cursor: pointer;
                    font-size: 12px;
                    font-weight: 500;
                    color: #374151;
                    transition: all 0.2s ease;
                    text-align: center;
                }

                .size-btn:hover {
                    border-color: #3b82f6;
                    background: #eff6ff;
                    color: #1e40af;
                }

                .size-btn.active {
                    border-color: #3b82f6;
                    background: #3b82f6;
                    color: white;
                    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
                }

                @media (max-width: 768px) {
                    .overlay-toggles {
                        grid-template-columns: 1fr;
                    }
                }

                /* Room Material Customization */
                .section-description {
                    font-size: 13px;
                    color: #64748b;
                    margin-bottom: 16px;
                    line-height: 1.4;
                }

                .room-selector-map {
                    margin-bottom: 16px;
                }

                .room-map-svg {
                    width: 100%;
                    height: 150px;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                }

                .room-map-placeholder {
                    width: 100%;
                    height: 150px;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #64748b;
                    font-size: 13px;
                }

                .room-selector-controls {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 8px;
                    align-items: center;
                    margin-top: 10px;
                }

                .selector-btn {
                    padding: 8px 16px;
                    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
                    color: white;
                    border: none;
                    border-radius: 8px;
                    font-size: 12px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .selector-btn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
                }

                .multi-select-toggle.active {
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
                }

                .multi-select-toggle.active:hover {
                    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
                }

                .selector-hint {
                    font-size: 11px;
                    color: #64748b;
                    margin-left: auto;
                }

                .material-section {
                    margin-top: 24px;
                    padding: 16px;
                    background: #f8fafc;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                }

                .material-type-toggle {
                    display: flex;
                    gap: 8px;
                    margin-bottom: 16px;
                }

                .texture-search {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 12px;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    margin-bottom: 12px;
                }

                .texture-search svg {
                    color: #64748b;
                    flex-shrink: 0;
                }

                .search-input {
                    flex: 1;
                    border: none;
                    outline: none;
                    font-size: 14px;
                    color: #374151;
                }

                .search-input::placeholder {
                    color: #94a3b8;
                }

                .texture-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 8px;
                    max-height: 300px;
                    overflow-y: auto;
                    margin-bottom: 16px;
                }

                .texture-item {
                    cursor: pointer;
                    border: 2px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 8px;
                    background: white;
                    transition: all 0.2s ease;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 6px;
                }

                .texture-item:hover {
                    border-color: #3b82f6;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
                }

                .texture-item.selected {
                    border-color: #3b82f6;
                    background: #eff6ff;
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
                }

                .texture-preview {
                    width: 60px;
                    height: 60px;
                    border-radius: 6px;
                    border: 1px solid rgba(0, 0, 0, 0.1);
                }

                .texture-name {
                    font-size: 11px;
                    color: #374151;
                    text-align: center;
                    font-weight: 500;
                }

                .texture-item.selected .texture-name {
                    color: #1e40af;
                    font-weight: 600;
                }

                .apply-btn {
                    width: 100%;
                    padding: 12px;
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                    color: white;
                    border: none;
                    border-radius: 8px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .apply-btn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
                }

                .apply-btn:active {
                    transform: translateY(0);
                }

                .apply-btn:disabled {
                    background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
                    cursor: not-allowed;
                    opacity: 0.6;
                }

                .apply-btn:disabled:hover {
                    transform: none;
                    box-shadow: none;
                }

                .reset-btn {
                    padding: 12px;
                    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
                    color: white;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 44px;
                }

                .reset-btn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
                }

                .reset-btn:active {
                    transform: translateY(0);
                }

                .reset-btn:disabled {
                    background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
                    cursor: not-allowed;
                    opacity: 0.6;
                }

                .reset-btn:disabled:hover {
                    transform: none;
                    box-shadow: none;
                }

                .height-control {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                    padding: 16px;
                    background: white;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                }

                .height-control__label {
                    font-size: 14px;
                    font-weight: 600;
                    color: #374151;
                    text-align: center;
                }

                .height-control__slider {
                    width: 100%;
                    height: 6px;
                    border-radius: 3px;
                    background: #e2e8f0;
                    outline: none;
                    -webkit-appearance: none;
                    appearance: none;
                }

                .height-control__slider::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    width: 20px;
                    height: 20px;
                    border-radius: 50%;
                    background: #3b82f6;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .height-control__slider::-webkit-slider-thumb:hover {
                    background: #2563eb;
                    transform: scale(1.1);
                }

                .height-control__slider::-moz-range-thumb {
                    width: 20px;
                    height: 20px;
                    border-radius: 50%;
                    background: #3b82f6;
                    cursor: pointer;
                    border: none;
                    transition: all 0.2s ease;
                }

                .height-control__slider::-moz-range-thumb:hover {
                    background: #2563eb;
                    transform: scale(1.1);
                }

                .height-control__markers {
                    display: flex;
                    justify-content: space-between;
                    font-size: 11px;
                    color: #64748b;
                    padding: 0 4px;
                }

                /* Responsive Design for Mobile */
                @media (max-width: 480px) {
                    .collapsible-section__header {
                        padding: 14px 16px;
                    }

                    .collapsible-section__title {
                        font-size: 14px;
                    }

                    .collapsible-section__content {
                        padding: 0 16px 16px;
                    }

                    .section-description {
                        font-size: 12px;
                        margin-bottom: 12px;
                    }

                    .room-map-svg,
                    .room-map-placeholder {
                        height: 120px;
                    }

                    .room-selector-controls {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .selector-btn {
                        padding: 10px 12px;
                        font-size: 13px;
                        width: 100%;
                    }

                    .selector-hint {
                        text-align: center;
                        margin-top: 4px;
                        margin-left: 0;
                        font-size: 10px;
                    }

                    .material-section {
                        padding: 12px;
                        margin-top: 16px;
                    }

                    .material-type-toggle {
                        margin-bottom: 12px;
                    }

                    .toggle-btn {
                        padding: 8px 12px;
                        font-size: 11px;
                    }

                    .texture-grid {
                        grid-template-columns: repeat(2, 1fr);
                        gap: 6px;
                        max-height: 200px;
                    }

                    .texture-item {
                        padding: 6px;
                    }

                    .texture-preview {
                        width: 50px;
                        height: 50px;
                    }

                    .texture-name {
                        font-size: 10px;
                    }

                    .apply-btn {
                        padding: 10px;
                        font-size: 13px;
                    }

                    .reset-btn {
                        padding: 10px;
                        min-width: 40px;
                    }

                    .color-picker {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 8px;
                    }

                    .color-picker__input {
                        width: 100%;
                        height: 40px;
                    }

                    .color-picker__preview {
                        width: 100%;
                        height: 30px;
                    }
                }

                /* Tablet responsive */
                @media (min-width: 481px) and (max-width: 768px) {
                    .room-map-svg,
                    .room-map-placeholder {
                        height: 130px;
                    }

                    .texture-grid {
                        grid-template-columns: repeat(3, 1fr);
                        max-height: 220px;
                    }

                    .texture-preview {
                        width: 55px;
                        height: 55px;
                    }
                }
            `}</style>
        </div>
    );
};